import React, { useEffect } from 'react';
import { hooks } from '@lifesize/clients.sdk';
import { connect, useDispatch } from 'react-redux';
import _compact from 'lodash/compact';
import _get from 'lodash/get';
import {
  accountSelectors,
  chatSelectors,
  contactSelectors,
  featureSelectors,
  userProfileSelectors,
  currentUserSelectors,
  directoryActions
} from '@lifesize/nucleus';
import {
  closeInCallDrawer,
  toggleInCallDrawer,
  openAVSettingsModal,
  requestRemoteGroupUuid,
  resetRemoteGroupUuid
} from 'actions/InCall/InCallDrawer';
import Drawer from 'components/InCall/InCallDrawerHolder';
import { gqlChatAvailableForCall, gqlChatContactForCall } from '../../../utils/chat';
import { tooltipNotifications } from 'constants/Notification';
import {
  CALL_DETAILS_DRAWER,
  CHAT_DRAWER,
  COLLABORATION_DRAWER,
  PARTICIPANTS_DRAWER
} from 'constants/InCall/InCallDrawer';
import { ADD_PARTICIPANT_DRAWER } from 'constants/AddParticipantDrawer';
import { FLAG_IN_CALL_COLLABORATION } from 'constants/FeatureFlags';
import { isMobile, isChrome } from 'utils/browser-utils';

/**
 * This container will hold all of the controls for the in call drawer.
 * Currently only tracking state and holds actions whether to open or close the
 * drawer.
 *
 * This will manage subcomponents much like FullScreenModalContainer.
 */

const mapDispatchToProps = (dispatch) => ({
  clearRemoteGroupUuid: () => {
    dispatch(resetRemoteGroupUuid());
  },
  onCloseClicked: () => dispatch(closeInCallDrawer()),
  onToggleClicked: (subComponentType, drawerProps) => dispatch(toggleInCallDrawer(subComponentType, drawerProps)),
  openAV: () => dispatch(openAVSettingsModal()),
  requestRemoteGroupUuid: (extension) => {
    dispatch(requestRemoteGroupUuid(extension));
  }
});

const mapStateToProps = (state, ownProps) => {
  const conferenceUuid = _get(ownProps, 'conference.uuid');
  const remoteContact = conferenceUuid
    ? contactSelectors.getContactByUuidGenerator(state)(conferenceUuid)
    : contactSelectors.getContactByDialStringGenerator(state)(_get(ownProps, 'call.dialString'));

  const extension = _get(remoteContact, 'extension');
  const chatContact = gqlChatContactForCall(state, ownProps.conference, ownProps.participants);

  const unreadChats = _get(chatSelectors.getChatForWebGenerator(state)(chatContact), 'unreadMessageCount');

  const isGuest = userProfileSelectors.selectIsGuest(state);
  // ownProps.participants.length is for waiting room participants. They have no information about the conference, so 0 participants means waiting room user.
  const showAddParticipantDrawer = !isGuest && ownProps.participants.length;
  const showCallDetailsDrawer = !isGuest || (remoteContact && extension);
  // Personal (per user) Contacts and Group (per account) Contacts will have the remoteContact populated but may be cross-group
  // the extension is needed for the cross-group check (prevent the api call if the extension is not defined)
  const checkForCrossGroup =
    !!extension &&
    (!remoteContact || _get(remoteContact, 'isPersonalContact') || _get(remoteContact, 'isGroupContact'));
  const localGroupUuid = currentUserSelectors.myInfo(state).groupId;
  const remoteGroupUuid = checkForCrossGroup ? _get(state, 'app.inCallDrawer.remoteGroupUuid') : localGroupUuid;
  const isCrossGroup = !isGuest && localGroupUuid !== remoteGroupUuid;
  const wbSupported = isChrome() && !isMobile();
  const isVMR = _get(ownProps, 'conference.type') === 'VMR'; // conference type is VRM for permanent and OTM VMRs
  const whiteboardFeatureEnabled = !!_get(accountSelectors.getAccountFeatures(state), 'whiteboarding');
  const whiteboardFlagEnabled = featureSelectors.getFeatureFlag(state)(FLAG_IN_CALL_COLLABORATION);
  const showCollaborationDrawer =
    wbSupported && whiteboardFlagEnabled && isVMR && whiteboardFeatureEnabled && !isCrossGroup;
  // This is for waiting room participants. They have no information about the conference, so 0 participants means waiting room user.
  const showParticipantsDrawer = ownProps.participants.length;

  const showChatDrawer = gqlChatAvailableForCall(state, ownProps.conference, ownProps.participants);
  const raisedHandsCount = ownProps?.participantsWithRaisedHands?.length || 0;
  const btnNotificationRaisedHand =
    _get(state, `app.notification.tooltips.${tooltipNotifications.inCallDrawerBtnRaiseHand}`) || undefined;
  const availableDrawers = _compact([
    showAddParticipantDrawer ? ADD_PARTICIPANT_DRAWER : null,
    showCallDetailsDrawer ? CALL_DETAILS_DRAWER : null,
    showParticipantsDrawer ? PARTICIPANTS_DRAWER : null,
    showChatDrawer ? CHAT_DRAWER : null,
    showCollaborationDrawer ? COLLABORATION_DRAWER : null
  ]);
  const subComponentType = _get(state, 'app.inCallDrawer.subComponentType');
  // If a drawer is selected, open it if it is available. Otherwise fall back to the first available drawer
  const visibleComponent = !!subComponentType
    ? availableDrawers.find((drawer) => drawer === subComponentType)
      ? subComponentType
      : availableDrawers[0]
    : null;

  return {
    ...ownProps,
    availableDrawers,
    btnNotificationRaisedHand,
    checkForCrossGroup,
    extension,
    isGuest,
    raisedHandsCount,
    unreadChats,
    visibleComponent
  };
};

const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(Drawer);

const FunctionalWrapper = (props) => {
  const conference = hooks.useConferenceInfo();
  const call = hooks.useCallState();
  const isModerator = hooks.useIsCurrentUserModerator();
  const participants = hooks.useParticipants();
  const participantsWithRaisedHands = hooks.useParticipantsWithRaisedHands();
  const conferenceLocked = hooks.useIsWaitingRoomEnabled();
  const dispatch = useDispatch();
  const conferenceUuid = conference && conference.uuid;
  const waitingRoomUsers = hooks.useWaitingRoomUsers();
  const waitingRoomUsersCount = waitingRoomUsers?.length || 0;

  useEffect(() => {
    if (conferenceUuid) {
      dispatch(directoryActions.loadContactIfNotPresent(conferenceUuid)); // Load the conference into the state if it doesn't already exist
    }
  }, [dispatch, conferenceUuid]);

  return (
    <ConnectedComponent
      conference={conference}
      participants={participants}
      call={call}
      isModerator={isModerator}
      isLockedByModerator={conferenceLocked && !isModerator}
      participantsWithRaisedHands={participantsWithRaisedHands}
      waitingRoomUsersCount={waitingRoomUsersCount}
      {...props}
    />
  );
};

export default FunctionalWrapper;
